/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import {
  useLocalization,
  Loading,
  ErrorRenderer,
  ResponsiveTable,
} from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { PaymentIntegration, PaymentIntegrationType } from "@lemmonhq/types";
import { setTitle } from "@lemmonhq/orders";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import {
  getIntegrationIcon,
  getIntegrationLabel,
  getIntegrationLogo,
  getIntegrationUsageIcon,
  getIntegrationUsageLabel,
} from "./utils";
import { PaymentIntegrationForm } from "./PaymentIntegrationForm";
import { EmptyView } from "./EmptyView";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

const getUsage = (integration: PaymentIntegration) =>
  (integration.settings as { type?: "online" | "tap2pay" | "terminal" })?.type;

const renderLogo = (type: PaymentIntegrationType) => {
  const logo = getIntegrationLogo(type);
  if (logo) {
    return (
      <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white p-1.5">
        <img src={logo} alt={type} class="h-full w-full object-contain" />
      </div>
    );
  }
  return (
    <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 via-primary/30 to-secondary/20">
      <span class="material-symbols-outlined text-xl text-primary">
        {getIntegrationIcon(type)}
      </span>
    </div>
  );
};

export const PaymentsSettings = defineComponent({
  name: "PaymentsSettings",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const integrations = ref<PaymentIntegration[]>([]);
    const loading = ref(true);
    const loadError = ref<Error | null>(null);
    const selectedIntegration = ref<PaymentIntegration | null>(null);
    const mode = ref<"create" | "edit" | null>();

    const loadData = async () => {
      try {
        loading.value = true;
        loadError.value = null;
        const result = await trpc.paymentIntegrations.list.query({});
        integrations.value = result.data || [];
      } catch (error) {
        loadError.value =
          error instanceof Error
            ? error
            : new Error("Failed to fetch payment integrations");
        console.error("Failed to fetch payment integrations:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      setTitle(t("settings.payments.title"));
      await loadData();
    });

    const openCreateSheet = () => {
      mode.value = "create";
      selectedIntegration.value = null;
    };

    const openEditSheet = (integration: PaymentIntegration) => {
      mode.value = "edit";
      selectedIntegration.value = integration;
    };

    const closeSheet = () => {
      selectedIntegration.value = null;
      mode.value = null;
    };

    const handleSubmit = () => {
      closeSheet();
      loadData();
    };

    const handleDelete = () => {
      closeSheet();
      loadData();
    };

    const columns: ResponsiveTableColumn[] = [
      // ── Desktop: Provider ─────────────────────────────────────────────────
      {
        key: "d-provider",
        label: "Provider",
        icon: "payment",
        mobilePriority: "hidden",
        render: (integration: PaymentIntegration) => (
          <div class="flex items-center gap-3">
            {renderLogo(integration.type)}
            <div class="font-medium text-base-content">
              {getIntegrationLabel(integration.type)}
            </div>
          </div>
        ),
      },
      // ── Desktop: Usage ────────────────────────────────────────────────────
      {
        key: "d-usage",
        label: "Usage",
        icon: "category",
        mobilePriority: "hidden",
        render: (integration: PaymentIntegration) => (
          <div class="badge badge-primary badge-sm gap-1">
            <span class="material-symbols-outlined text-xs">
              {getIntegrationUsageIcon(getUsage(integration))}
            </span>
            {getIntegrationUsageLabel(getUsage(integration))}
          </div>
        ),
      },
      // ── Desktop: Users ────────────────────────────────────────────────────
      {
        key: "d-users",
        label: "Users",
        icon: "group",
        mobilePriority: "hidden",
        render: (integration: PaymentIntegration) =>
          integration.user_ids.length > 0 ? (
            <span class="text-sm text-base-content/80">
              {integration.user_ids.length} user
              {integration.user_ids.length !== 1 ? "s" : ""}
            </span>
          ) : (
            <span class="text-sm text-base-content/40">All users</span>
          ),
      },
      // ── Desktop: Created ──────────────────────────────────────────────────
      {
        key: "d-created",
        label: "Created",
        icon: "schedule",
        mobilePriority: "hidden",
        render: (integration: PaymentIntegration) => (
          <span class="text-sm text-base-content/60">
            {new Date(integration.created_at).toLocaleDateString()}
          </span>
        ),
      },
      // ── Mobile primary: name + type badge ─────────────────────────────────
      {
        key: "m-name",
        label: "Provider",
        desktopHide: true,
        mobilePriority: "primary",
        render: (integration: PaymentIntegration) => (
          <div class="flex items-center gap-2 min-w-0">
            {renderLogo(integration.type)}
            <div class="font-medium text-sm text-base-content truncate">
              {getIntegrationLabel(integration.type)}
            </div>
          </div>
        ),
      },
      // ── Mobile primary: usage badge (right-aligned) ───────────────────────
      {
        key: "m-usage-badge",
        label: "Usage",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (integration: PaymentIntegration) => (
          <div class="badge badge-primary badge-sm gap-1">
            <span class="material-symbols-outlined text-xs">
              {getIntegrationUsageIcon(getUsage(integration))}
            </span>
            {getIntegrationUsageLabel(getUsage(integration))}
          </div>
        ),
      },
      // ── Mobile secondary: users ───────────────────────────────────────────
      {
        key: "m-users",
        label: "Users",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (integration: PaymentIntegration) =>
          integration.user_ids.length > 0
            ? `${integration.user_ids.length} user${integration.user_ids.length !== 1 ? "s" : ""}`
            : "All",
      },
      // ── Mobile secondary: created ─────────────────────────────────────────
      {
        key: "m-created",
        label: "Added",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (integration: PaymentIntegration) =>
          new Date(integration.created_at).toLocaleDateString(),
      },
      // ── Desktop actions ───────────────────────────────────────────────────
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (integration: PaymentIntegration) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openEditSheet(integration);
            }}
          >
            <span class="material-symbols-outlined text-2xl">table_edit</span>
          </button>
        ),
      },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="payment"
          title={t("settings.payments.title")}
          description={t("settings.payments.desc")}
        >
          {{
            actions: () => (
              <button
                disabled={loading.value}
                class="btn btn-primary btn-sm gap-2 shadow-lg"
                onClick={openCreateSheet}
              >
                <span class="material-symbols-outlined">add</span>
                Add integration
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding>
            {/* Loading */}
            {loading.value && (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            )}

            {/* Error */}
            {!loading.value && loadError.value && (
              <ErrorRenderer error={loadError.value} onRetry={loadData} />
            )}

            {/* Empty */}
            {!loading.value &&
              !loadError.value &&
              integrations.value.length === 0 && (
                <EmptyView
                  icon="payment"
                  title="No payment integrations"
                  description="Get started by adding your first payment integration"
                  actionLabel="Add integration"
                  onAction={openCreateSheet}
                />
              )}

            {/* Table */}
            {!loading.value &&
              !loadError.value &&
              integrations.value.length > 0 && (
                <ResponsiveTable
                  columns={columns}
                  rows={integrations.value}
                  rowKey={(i: PaymentIntegration) => i.id}
                  onRowClick={openEditSheet}
                  emptyMessage="No payment integrations"
                />
              )}
          </AppSubpageContent>
        </AppSubpage>

        {/* Right Sheet for Create/Edit */}
        {mode.value ? (
          <PaymentIntegrationForm
            mode={mode.value}
            integration={selectedIntegration.value}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onClose={closeSheet}
          />
        ) : null}
      </div>
    );
  },
});
